import { flatten, sum } from 'lodash';
import type {
  Suggestion,
  SuggestionLog,
  SuggestionLogEntry,
  SuggestionLogTree,
} from './getSuggestions';
import { mapTransform, setUnion } from '../../../lib/util';
import {
  mapGroupBy,
  mapGroupByMany,
  mapsMergeWith,
  objectsMergeWith,
} from '@seaber/common/lib/collections';

function createVesselReport(
  availableOrderIds: Set<number>,
  logs: SuggestionLogTree,
  suggestions: Suggestion[],
) {
  function collectRecursiveLogEntries(
    tree: SuggestionLogTree,
  ): SuggestionLogEntry[] {
    return [
      ...tree.logEntries,
      ...tree.branches.flatMap(collectRecursiveLogEntries),
    ];
  }
  const allLogEntries = collectRecursiveLogEntries(logs);

  /** Upcoming orders valid onto this vessel */
  const validOrders = new Set(
    allLogEntries
      .filter((e) => e.stage === 'preprocessing' && e.reason === 'is_valid')
      .flatMap((e) => e.orderIds),
  );

  const invalidOrderLogs = allLogEntries.filter(
    (e) => e.stage === 'preprocessing' && e.reason !== 'is_valid',
  );
  const invalidByReason = mapGroupBy(invalidOrderLogs, (e) => e.reason);

  const shortlistingAllEntries = allLogEntries.filter(
    (e) => e.stage === 'shortlisting',
  );
  const shortListingAcceptedEntries = shortlistingAllEntries.filter(
    (e) => e.reason === 'selected',
  );

  const shortlistingEvaluatedOrderIds = new Set(
    shortlistingAllEntries.flatMap((e) => e.orderIds),
  );
  const shortlistingAcceptedOrderIds = new Set(
    shortlistingAllEntries
      .filter((e) => e.reason === 'selected')
      .flatMap((e) => e.orderIds),
  );
  const alllRejectedShortlistingEntries = shortlistingAllEntries.filter(
    (e) => e.reason !== 'selected',
  );
  const shortlistingRejectedByReason = mapGroupBy(
    alllRejectedShortlistingEntries,
    (e) => e.reason,
  );
  const shortlistingRejectedOrderIdsByReason = mapTransform(
    shortlistingRejectedByReason,
    (es) => new Set(es.flatMap((e) => e.orderIds)),
  );
  const shortlistingEntriesByOrderId = mapGroupByMany(
    shortlistingAllEntries,
    (e) => e.orderIds,
  );

  const schedulingAllEntriesByReason = mapGroupBy(
    allLogEntries.filter((e) => e.stage === 'scheduling'),
    (e) => e.reason,
  );

  const allCombinationEntries = allLogEntries.filter(
    (e) => e.stage === 'combinations',
  );
  const combinationEvaluatedOrderIds = new Set(
    allCombinationEntries.flatMap((e) => e.orderIds),
  );
  const allAcceptedCombinationEntries = allCombinationEntries.filter(
    (e) => e.reason === 'is_valid',
  );
  const allRejectedCombinationEntries = allCombinationEntries.filter(
    (e) => e.reason !== 'is_valid',
  );
  const combinationAcceptedOrderIds = new Set(
    allAcceptedCombinationEntries.flatMap((e) => e.orderIds),
  );
  const combinationRejectedByReason = mapGroupBy(
    allRejectedCombinationEntries,
    (e) => e.reason,
  );
  const combinationRejectedOrderIdsByReason = mapTransform(
    combinationRejectedByReason,
    (es) => new Set(es.flatMap((e) => e.orderIds)),
  );
  const combinationEntriesByOrderId = mapGroupByMany(
    allCombinationEntries,
    (e) => e.orderIds,
  );

  const ordersInSuggestions = new Set(
    suggestions.flatMap((s) =>
      s.schedule.combination.assignments.map((a) => a.order.id),
    ),
  );

  return {
    orders: {
      available: availableOrderIds,
      valid: validOrders,
      invalidByReason,
      inSuggestions: ordersInSuggestions,
    },
    logEntries: allLogEntries,
    totalCombinationCount: allCombinationEntries.length,
    validCombinationCount: allAcceptedCombinationEntries.length,

    shortlistingAllEntries,
    shortListingAcceptedEntries,
    shortlistingEvaluatedOrderIds,
    shortlistingAcceptedOrderIds,
    shortlistingRejectedByReason,
    shortlistingRejectedOrderIdsByReason,
    shortlistingEntriesByOrderId,

    schedulingAllEntriesByReason,

    combinationEvaluatedOrderIds,
    combinationAcceptedOrderIds,
    combinationRejectedByReason,
    combinationRejectedOrderIdsByReason,
    combinationEntriesByOrderId,
  };
}

export function createReportFromLogs(
  logs: SuggestionLog,
  suggestionsByVessel: Map<number, Suggestion[]>,
  filterVessels?: Set<number>,
) {
  const vesselReports = new Map(
    [...logs.logsByVessel.entries()]
      .filter(([vesselId]) => !filterVessels || filterVessels?.has(vesselId))
      .map(([vesselId, vesselLogs]) => [
        vesselId,
        createVesselReport(
          logs.availableOrderIds,
          vesselLogs,
          suggestionsByVessel.get(vesselId) ?? [],
        ),
      ]),
  );

  const allLogEntries = [
    ...logs.logEntries,
    ...vesselReports.values().flatMap((v) => v.logEntries),
  ];

  const combined = objectsMergeWith([...vesselReports.values()], {
    orders: (items) =>
      objectsMergeWith(items, {
        available: setUnion,
        valid: setUnion,
        invalidByReason: mapsMergeWith(flatten),
        inSuggestions: setUnion,
      }),
    logEntries: flatten,

    totalCombinationCount: sum,
    validCombinationCount: sum,

    shortlistingAllEntries: flatten,
    shortlistingEvaluatedOrderIds: setUnion,
    shortListingAcceptedEntries: flatten,
    shortlistingAcceptedOrderIds: setUnion,
    shortlistingRejectedByReason: mapsMergeWith(flatten),
    shortlistingRejectedOrderIdsByReason: mapsMergeWith(setUnion),
    shortlistingEntriesByOrderId: mapsMergeWith(flatten),

    schedulingAllEntriesByReason: mapsMergeWith(flatten),

    combinationEvaluatedOrderIds: setUnion,
    combinationAcceptedOrderIds: setUnion,
    combinationRejectedByReason: mapsMergeWith(flatten),
    combinationRejectedOrderIdsByReason: mapsMergeWith(setUnion),
    combinationEntriesByOrderId: mapsMergeWith(flatten),
  });

  const invalidTopLevelOrderLogs = logs.logEntries.filter(
    (e) => e.reason !== 'is_valid',
  );
  const topLevelInvalidByReason = mapGroupBy(
    invalidTopLevelOrderLogs,
    (e) => e.reason,
  );

  return {
    ...combined,
    vesselReports,
    allLogEntries,
    topLevelInvalidByReason,
  };
}
